/**
 * shadcn/Tailwind replacement for UnsavedChangesConfirmDialog.
 * Confirmation dialog for discarding unsaved changes.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface ShadcnUnsavedChangesDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    mode: 'create' | 'edit';
    entityType: string;
}

export const ShadcnUnsavedChangesDialog: React.FC<ShadcnUnsavedChangesDialogProps> = ({
    open,
    onClose,
    onConfirm,
    mode,
    entityType,
}) => {
    if (!open) return null;

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    const title = mode === 'create'
        ? `Discard new ${entityType}?`
        : `Discard changes to ${entityType}?`;

    const message = mode === 'create'
        ? `You have unsaved changes. Are you sure you want to close without creating this ${entityType.toLowerCase()}?`
        : `You have unsaved changes. Are you sure you want to close without saving your changes?`;

    return (
        <div className="fixed inset-0 z-300 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />

            {/* Dialog */}
            <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-lg mx-4">
                {/* Header */}
                <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
                    <AlertTriangle size={20} className="text-yellow-600 dark:text-yellow-400 shrink-0" />
                    <h2 className="text-base font-semibold text-foreground">{title}</h2>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                    <p className="text-sm text-muted-foreground">{message}</p>

                    <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/50 p-3">
                        <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                        <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                            All unsaved changes will be lost.
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
                    <button
                        onClick={onClose}
                        className="h-9 px-4 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-muted text-foreground"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="h-9 px-4 rounded-md text-sm font-medium transition-colors bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700"
                    >
                        Discard Changes
                    </button>
                </div>
            </div>
        </div>
    );
};
